import React, { useState, useEffect } from 'react';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import { SkeletonSettings, EmptyState } from '@/components/ui/hayat';
import PushNotificationSettings from '@/components/family/PushNotificationSettings';
import axios from 'axios';

/**
 * Phase 15.4 - Integrated Notifications Page
 * With Skeleton Loaders and Empty States
 */
function FamilyNotifications() {
  const { familyUser, household } = useFamilyAuth();
  const [preferences, setPreferences] = useState(null);
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (familyUser) {
      loadData();
    }
  }, [familyUser]);

  const loadData = async () => {
    try {
      // Preferences laden
      const prefsRes = await axios.get(
        `${BACKEND_URL}/api/notification/preferences/${familyUser.id}`
      );
      setPreferences(prefsRes.data);

      // Events laden
      const eventsRes = await axios.get(
        `${BACKEND_URL}/api/notification/events?household_id=${household.id}`
      );
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Fehler beim Laden der Benachrichtigungen:', error);
      // Fallback: Standard-Preferences
      setPreferences({
        family_user_id: familyUser.id,
        channels: ['email'],
        is_emergency_enabled: true,
        is_reminder_enabled: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (field) => {
    const newPreferences = {
      ...preferences,
      [field]: !preferences[field]
    };
    
    setPreferences(newPreferences);
    await savePreferences(newPreferences);
  };

  const handleChannelToggle = async (channel) => {
    const channels = preferences.channels || [];
    const newChannels = channels.includes(channel)
      ? channels.filter(c => c !== channel)
      : [...channels, channel];
    
    const newPreferences = {
      ...preferences,
      channels: newChannels
    };
    
    setPreferences(newPreferences);
    await savePreferences(newPreferences);
  };

  const savePreferences = async (prefs) => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/notification/preferences/${familyUser.id}`,
        prefs
      );
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Einstellungen konnten nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const getEventStatusColor = (status) => {
    const colors = {
      'sent': '[#6DCEE8]',
      'delivered': '[#22C55E]',
      'failed': '[#EF4444]',
      'partial': '[#F6B352]'
    };
    return colors[status] || 'neutral-500';
  };

  const getEventStatusLabel = (status) => {
    const labels = {
      'sent': 'Gesendet',
      'delivered': 'Zugestellt',
      'failed': 'Fehlgeschlagen',
      'partial': 'Teilweise'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050816] pb-24 page-transition">
        <div className="bg-neutral-900/60 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="animate-pulse bg-neutral-700 h-10 w-64 rounded-xl mb-2"></div>
            <div className="animate-pulse bg-neutral-700 h-6 w-40 rounded-xl"></div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <SkeletonSettings />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] pb-24 page-transition">
      {/* Header */}
      <div className="bg-neutral-900/60 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40 glass-effect">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="animate-fade-in-down">
            <h1 className="text-3xl font-bold text-[#F9FAFB] mb-1">
              Benachrichtigungen 🔔
            </h1>
            <p className="text-neutral-500 font-medium">Einstellungen & Verlauf</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Push Notification Settings */}
        <div className="stagger-item stagger-delay-1">
          <PushNotificationSettings />
        </div>

        {/* Einstellungen Section */}
        <div className="stagger-item stagger-delay-2 bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-md hover:shadow-hayat-lg transition-all duration-300 border border-neutral-600/40">
          <h2 className="text-2xl font-bold text-[#F9FAFB] mb-6 flex items-center gap-2">
            <span>⚙️</span>
            Einstellungen
          </h2>

          <div className="space-y-4">
            {/* Notfall-Benachrichtigungen */}
            <div className="flex items-center justify-between p-6 bg-hayat-danger-50 rounded-2xl border border-hayat-danger-100">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-[#F9FAFB] mb-1 flex items-center gap-2">
                  <span className="text-2xl">🚨</span>
                  Notfall-Benachrichtigungen
                </h3>
                <p className="text-sm text-neutral-500">Sofortige Benachrichtigung bei Notfällen</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences?.is_emergency_enabled || false}
                  onChange={() => handleToggle('is_emergency_enabled')}
                  disabled={saving}
                  className="sr-only peer"
                />
                <div className="w-14 h-8 bg-hayat-neutral-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-hayat-primary-300 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-hayat-neutral-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-hayat-success-500"></div>
              </label>
            </div>

            {/* Erinnerungs-Benachrichtigungen */}
            <div className="flex items-center justify-between p-6 bg-hayat-warning-50 rounded-2xl border border-hayat-warning-100">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-[#F9FAFB] mb-1 flex items-center gap-2">
                  <span className="text-2xl">⏰</span>
                  Erinnerungs-Benachrichtigungen
                </h3>
                <p className="text-sm text-neutral-500">Benachrichtigung bei Erinnerungen</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences?.is_reminder_enabled || false}
                  onChange={() => handleToggle('is_reminder_enabled')}
                  disabled={saving}
                  className="sr-only peer"
                />
                <div className="w-14 h-8 bg-hayat-neutral-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-hayat-primary-300 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-hayat-neutral-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-hayat-success-500"></div>
              </label>
            </div>

            {/* Benachrichtigungs-Kanäle */}
            <div className="p-6 bg-hayat-secondary-50 rounded-2xl border border-hayat-secondary-100">
              <h3 className="text-lg font-bold text-[#F9FAFB] mb-3 flex items-center gap-2">
                <span className="text-2xl">📧</span>
                Benachrichtigungs-Kanäle
              </h3>
              <p className="text-sm text-neutral-500 mb-4">Wählen Sie, wie Sie benachrichtigt werden möchten</p>

              <div className="space-y-3">
                {/* E-Mail */}
                <label className="flex items-center gap-3 p-4 bg-white rounded-xl cursor-pointer hover:bg-neutral-800/50 transition-colors border border-hayat-neutral-200">
                  <input
                    type="checkbox"
                    checked={preferences?.channels?.includes('email') || false}
                    onChange={() => handleChannelToggle('email')}
                    disabled={saving}
                    className="w-5 h-5 text-hayat-primary-500 bg-hayat-neutral-100 border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2"
                  />
                  <span className="text-lg">📧</span>
                  <span className="font-medium text-[#F9FAFB]">E-Mail</span>
                </label>

                {/* Push */}
                <label className="flex items-center gap-3 p-4 bg-white rounded-xl cursor-pointer hover:bg-neutral-800/50 transition-colors border border-hayat-neutral-200">
                  <input
                    type="checkbox"
                    checked={preferences?.channels?.includes('push') || false}
                    onChange={() => handleChannelToggle('push')}
                    disabled={saving || !familyUser?.push_token}
                    className="w-5 h-5 text-hayat-primary-500 bg-hayat-neutral-100 border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2 disabled:opacity-50"
                  />
                  <span className="text-lg">🔔</span>
                  <span className="font-medium text-[#F9FAFB]">
                    Push {!familyUser?.push_token && '(nicht verfügbar)'}
                  </span>
                </label>

                {/* SMS (disabled) */}
                <label className="flex items-center gap-3 p-4 bg-hayat-neutral-100 rounded-xl opacity-50 cursor-not-allowed border border-hayat-neutral-200">
                  <input
                    type="checkbox"
                    disabled
                    className="w-5 h-5 text-hayat-primary-500 bg-hayat-neutral-100 border-hayat-neutral-300 rounded"
                  />
                  <span className="text-lg">📱</span>
                  <span className="font-medium text-neutral-500">SMS (bald verfügbar)</span>
                </label>
              </div>
            </div>
          </div>

          {saving && (
            <div className="mt-6 p-4 bg-hayat-success-50 border border-hayat-success-200 rounded-2xl text-center">
              <span className="text-hayat-success-700 font-semibold flex items-center justify-center gap-2">
                <span>✓</span>
                Gespeichert
              </span>
            </div>
          )}
        </div>

        {/* Benachrichtigungs-Verlauf */}
        <div className="stagger-item stagger-delay-3 bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-md hover:shadow-hayat-lg transition-all duration-300 border border-neutral-600/40">
          <h2 className="text-2xl font-bold text-[#F9FAFB] mb-6 flex items-center gap-2">
            <span>📜</span>
            Verlauf
          </h2>

          {events.length === 0 ? (
            <EmptyState
              icon="📭"
              title="Keine Benachrichtigungen"
              description="Es wurden noch keine Benachrichtigungen versendet"
            />
          ) : (
            <div className="space-y-3">
              {events.slice(0, 20).map(event => (
                <div
                  key={event.id}
                  className="flex items-start gap-4 p-5 bg-neutral-800/50 rounded-2xl hover:bg-hayat-neutral-100 transition-colors border border-hayat-neutral-200"
                >
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-2xl shadow-hayat-sm flex-shrink-0">
                    {event.type === 'emergency' && '🚨'}
                    {event.type === 'reminder' && '⏰'}
                    {event.type === 'system' && 'ℹ️'}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-[#F9FAFB] mb-1">
                      {event.payload?.title || event.type}
                    </h4>
                    <p className="text-sm text-neutral-500">
                      {new Date(event.created_at).toLocaleString('de-DE')}
                    </p>
                  </div>

                  {/* Status Badge */}
                  <div className={`px-4 py-2 rounded-xl text-xs font-bold text-white bg-${getEventStatusColor(event.status)} flex-shrink-0`}>
                    {getEventStatusLabel(event.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FamilyNotifications;
